import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Icon } from '../components/ui/Icon';
import { DarkModeToggle } from '../components/ui/DarkModeToggle';
import { useTheme } from '../styles/ThemeProvider';
import { FormField, SelectField } from '../components/forms';
import { useAuthStore } from '../stores/authStore';
import { profileSchemas } from '../components/forms/validation';
import { RestaurantProfileForm, UserProfileForm } from '../types/forms';
import { apiService } from '../services/api';
import { getCurrencyForCountryCode } from '@data/countries-cities';
import { useCountryCitySelection } from '@hooks/useCountryCitySelection';
import { createFormLogger } from '../utils/logger';

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 52rem;
  margin: 0;
  padding: 0;
`;

const PageHeader = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled(SharedPageTitle)``;

const PageDescription = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const ProfileSection = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const FormFields = styled.fieldset`
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
  &:disabled input,
  &:disabled select,
  &:disabled textarea,
  &:disabled button[role='combobox'] {
    background: var(--surface-strong);
    color: var(--text-secondary);
    cursor: default;
  }
`;

const SectionIcon = styled.span`
  font-size: 1.2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  /* Mobile devices - single column */
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormRow = styled.div`
  grid-column: 1 / -1;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;

  /* Mobile devices - stack buttons vertically */
  @media (max-width: 767px) {
    flex-direction: column-reverse;
    gap: 0.75rem;

    button {
      width: 100%;
    }
  }
`;

const ThemeToggleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--surface-subtle);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);

  /* Dark mode adjustments */
  [data-theme='dark'] & {
    background: var(--surface-muted);
    border-color: var(--border-strong);
  }
`;

const ThemeToggleLabel = styled.label`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
`;

const ThemeToggleDescription = styled.p`
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
  line-height: var(--line-height-normal);
`;

const ErrorMessage = styled.div`
  background: var(--danger-50);
  border: 1px solid var(--danger-100);
  border-radius: var(--radius-md);
  padding: 1rem;
  color: var(--danger-600);
  margin-bottom: 1rem;
`;

// ============================================================================
// Constants
// ============================================================================

const cuisineTypes = [
  { value: 'mediterranea', label: 'Mediterránea' },
  { value: 'italiana', label: 'Italiana' },
  { value: 'asiatica', label: 'Asiática' },
  { value: 'mexicana', label: 'Mexicana' },
  { value: 'francesa', label: 'Francesa' },
  { value: 'americana', label: 'Americana' },
  { value: 'vegetariana', label: 'Vegetariana' },
  { value: 'vegana', label: 'Vegana' },
  { value: 'fusion', label: 'Fusión' },
  { value: 'tradicional', label: 'Tradicional' },
  { value: 'otra', label: 'Otra' },
];

// Countries list now comes from comprehensive data via hooks

const CUSTOM_REGION_OPTION_VALUE = '__custom_region__';
const CUSTOM_REGION_OPTION_LABEL = 'Otro / Escribir…';

const languages = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
];

// ============================================================================
// Component
// ============================================================================

/**
 * ProfilePage - User and restaurant profile management
 *
 * Features:
 * - Edit restaurant profile information
 * - Edit user profile settings
 * - Form validation with Yup schemas
 * - Loading and error states
 * - Responsive design
 * - Toast notifications for success/error
 */
export const ProfilePage: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);
  const setProfile = useAuthStore((state) => state.setProfile);
  const setUser = useAuthStore((state) => state.setUser);
  const { theme, setTheme } = useTheme();
  const [draftTheme, setDraftTheme] = useState(theme);
  const [isEditingRestaurant, setIsEditingRestaurant] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSubmittingRestaurant, setIsSubmittingRestaurant] = useState(false);
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCustomRegion, setIsCustomRegion] = useState(false);
  const customRegionInputRef = useRef<HTMLInputElement | null>(null);
  const formLogger = React.useMemo(() => createFormLogger('ProfilePage'), []);
  useEffect(() => {
    if (!isEditingUser) setDraftTheme(theme);
  }, [theme, isEditingUser]);

  // Country/City selection hook - initialize with profile country if available
  const {
    cities,
    selectedCountry,
    setSelectedCountry,
    selectedCity,
    setSelectedCity,
    filteredCountries,
    filteredCities,
    resetCitySelection,
  } = useCountryCitySelection({
    ...(profile?.country && { defaultCountry: profile.country }),
    enableSearch: true,
  });

  // Restaurant profile form
  const restaurantForm = useForm<RestaurantProfileForm>({
    resolver: yupResolver(profileSchemas.restaurantProfile) as any,
    mode: 'onBlur',
  });

  // Watch country changes to automatically update currency
  const watchedCountry = restaurantForm.watch('country');

  // Single unified effect for country/city state synchronization
  useEffect(() => {
    // Only proceed if we have a valid country change
    if (!isEditingRestaurant || !watchedCountry) return;

    // Check if country actually changed to prevent unnecessary updates
    if (watchedCountry === selectedCountry) return;

    // Update country-related state
    setSelectedCountry(watchedCountry);
    setIsCustomRegion(false);
    const currencyCode = getCurrencyForCountryCode(watchedCountry);
    restaurantForm.setValue('currency', currencyCode, {
      shouldValidate: false,
    });

    // Reset city selection when country changes (but not on initial load)
    if (selectedCountry && selectedCity) {
      resetCitySelection();
      restaurantForm.setValue('city', '', { shouldValidate: false });
    }
  }, [
    isEditingRestaurant,
    watchedCountry,
    selectedCountry,
    selectedCity,
    setSelectedCountry,
    resetCitySelection,
    restaurantForm,
  ]);

  // User profile form
  const userForm = useForm<UserProfileForm>({
    resolver: yupResolver(profileSchemas.userProfile) as any,
    mode: 'onBlur',
  });

  // Load profile data on mount
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        setIsLoadingProfile(true);
        setError(null);

        // Load restaurant profile
        if (profile) {
          restaurantForm.reset({
            restaurant_name: profile.restaurant_name,
            country: profile.country,
            city: profile.city,
            cuisine_type: profile.cuisine_type,
            daily_covers: profile.daily_covers,
            currency: profile.currency,
            ...(profile.description
              ? { description: profile.description }
              : {}),
            ...(profile.website ? { website: profile.website } : {}),
            ...(profile.phone ? { phone: profile.phone } : {}),
            ...(profile.address ? { address: profile.address } : {}),
          });

          userForm.reset({
            name: user?.name ?? '',
            email: user?.email ?? '',
            ...(user?.phone ? { phone: user.phone } : {}),
            ...(user?.language ? { language: user.language } : {}),
            ...(user?.timezone ? { timezone: user.timezone } : {}),
            notifications_enabled: user?.notifications_enabled ?? false,
          });
        }

        formLogger.info('Profile data updated from state', undefined, {
          hasProfile: Boolean(profile),
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        formLogger.error('Failed loading profile data', error);
        toast.error('Error al cargar los datos del perfil');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfileData();
  }, [profile, user, restaurantForm, userForm, formLogger]);

  // Handle restaurant profile submission
  const onSubmitRestaurant = async (data: RestaurantProfileForm) => {
    if (!isEditingRestaurant || isSubmittingRestaurant) return;
    try {
      setIsSubmittingRestaurant(true);
      setError(null);

      const savedProfile = await apiService.updateRestaurantProfile(data);
      setProfile(savedProfile);
      restaurantForm.reset(data);
      setIsEditingRestaurant(false);

      toast.success('Perfil del restaurante actualizado correctamente');
    } catch (err: any) {
      const error = err instanceof Error ? err : new Error(String(err));
      formLogger.error('Failed updating restaurant profile', error);
      const errorMessage =
        err?.response?.data?.detail ||
        'Error al actualizar el perfil del restaurante';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmittingRestaurant(false);
    }
  };

  // Handle user profile submission
  const onSubmitUser = async (data: UserProfileForm) => {
    if (!isEditingUser || isSubmittingUser) return;
    try {
      setIsSubmittingUser(true);
      setError(null);

      // TODO: Implement user profile update API
      // Preserve the existing session-only behavior until that endpoint exists.
      if (user) setUser({ ...user, ...data });
      setTheme(draftTheme);
      toast.success('Configuración personal guardada en esta sesión');

      // Reset to reflect saved state (until API is implemented)
      userForm.reset(data);
      setIsEditingUser(false);
    } catch (err: any) {
      const error = err instanceof Error ? err : new Error(String(err));
      formLogger.error('Failed updating user profile', error);
      const errorMessage =
        err?.response?.data?.detail ||
        'Error al actualizar la configuración de usuario';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmittingUser(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <PageContainer>
        <LoadingSpinner message="Cargando perfil..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Mi Perfil</PageTitle>
        <PageDescription>
          Gestiona la información de tu restaurante y configuración personal
        </PageDescription>
      </PageHeader>

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      {/* Restaurant Profile Section */}
      <ProfileSection>
        <Card>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon role="img" aria-label="Restaurante">
                <Icon name="store" size="lg" />
              </SectionIcon>
              Información del Restaurante
            </SectionTitle>
            {!isEditingRestaurant && (
              <Button
                variant="secondary"
                startIcon={<Icon name="edit" size="sm" />}
                aria-label="Editar restaurante"
                disabled={isEditingUser}
                onClick={() => {
                  setError(null);
                  setIsEditingRestaurant(true);
                }}
              >
                Editar
              </Button>
            )}
          </SectionHeader>

          <form onSubmit={restaurantForm.handleSubmit(onSubmitRestaurant)}>
            <FormFields
              disabled={!isEditingRestaurant || isSubmittingRestaurant}
              aria-label="Datos del restaurante"
            >
              <FormGrid>
                <FormField
                  label="Nombre del Restaurante"
                  name="restaurant_name"
                  register={restaurantForm.register}
                  error={restaurantForm.formState.errors.restaurant_name}
                  required
                  placeholder="Ej: La Tavola Italiana"
                />

                <Controller
                  name="country"
                  control={restaurantForm.control}
                  render={({ field, fieldState }) => (
                    <SelectField
                      name="country"
                      label="País"
                      options={filteredCountries}
                      value={field.value}
                      onChange={(value) => {
                        const stringValue = Array.isArray(value)
                          ? value[0]
                          : value;
                        // Update both form state and hook state for consistency
                        field.onChange(stringValue);
                        setSelectedCountry(stringValue || undefined);
                      }}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      required
                      searchable
                      placeholder="Selecciona un país"
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={restaurantForm.control}
                  render={({ field, fieldState }) => (
                    <>
                      <SelectField
                        id="profile-city-select"
                        name="city"
                        label="Región / Provincia / Estado"
                        options={[
                          ...(!isCustomRegion &&
                          typeof field.value === 'string' &&
                          field.value.trim() !== '' &&
                          !filteredCities.some(
                            (opt) => opt.value === field.value,
                          )
                            ? [{ value: field.value, label: field.value }]
                            : []),
                          ...filteredCities,
                          {
                            value: CUSTOM_REGION_OPTION_VALUE,
                            label: CUSTOM_REGION_OPTION_LABEL,
                          },
                        ]}
                        value={
                          isCustomRegion
                            ? CUSTOM_REGION_OPTION_VALUE
                            : (field.value ?? '')
                        }
                        onChange={(value) => {
                          const stringValue = Array.isArray(value)
                            ? value[0]
                            : (value ?? '');

                          if (stringValue === CUSTOM_REGION_OPTION_VALUE) {
                            setIsCustomRegion(true);
                            setTimeout(() => {
                              customRegionInputRef.current?.focus();
                            }, 0);
                            return;
                          }

                          setIsCustomRegion(false);

                          // Update form state and hook state together for regions
                          field.onChange(stringValue);
                          setSelectedCity(stringValue || undefined);
                        }}
                        onBlur={field.onBlur}
                        error={
                          isCustomRegion ? undefined : fieldState.error?.message
                        }
                        required
                        searchable
                        placeholder={
                          selectedCountry
                            ? 'Selecciona una región/provincia/estado'
                            : 'Selecciona un país primero'
                        }
                        disabled={!selectedCountry || cities.length === 0}
                      />

                      {isCustomRegion && (
                        <FormRow>
                          <FormField
                            id="profile-city-custom"
                            label="Otra región / provincia / estado"
                            name="city"
                            type="text"
                            placeholder="Escribe una región/provincia/estado"
                            required
                            value={
                              typeof field.value === 'string' ? field.value : ''
                            }
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              setSelectedCity(e.target.value || undefined);
                            }}
                            onBlur={field.onBlur}
                            error={fieldState.error?.message}
                            ref={(el) => {
                              customRegionInputRef.current = el;
                              field.ref(el);
                            }}
                          />
                        </FormRow>
                      )}
                    </>
                  )}
                />

                <FormRow>
                  <FormField
                    label="Dirección"
                    name="address"
                    register={restaurantForm.register}
                    error={restaurantForm.formState.errors.address}
                    placeholder="Dirección completa del restaurante"
                  />
                </FormRow>

                <Controller
                  name="cuisine_type"
                  control={restaurantForm.control}
                  render={({ field, fieldState }) => (
                    <SelectField
                      name="cuisine_type"
                      label="Tipo de Cocina"
                      options={cuisineTypes}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      required
                      placeholder="Selecciona el tipo de cocina"
                    />
                  )}
                />

                <FormField
                  label="Cubiertos Diarios Promedio"
                  name="daily_covers"
                  type="number"
                  register={restaurantForm.register}
                  error={restaurantForm.formState.errors.daily_covers}
                  required
                  placeholder="Ej: 150"
                  min="1"
                  max="10000"
                />

                <FormField
                  label="Teléfono"
                  name="phone"
                  register={restaurantForm.register}
                  error={restaurantForm.formState.errors.phone}
                  placeholder="Ej: +34 123 456 789"
                />

                <FormRow>
                  <FormField
                    label="Sitio Web"
                    name="website"
                    register={restaurantForm.register}
                    error={restaurantForm.formState.errors.website}
                    placeholder="https://www.mirestaurante.com"
                  />
                </FormRow>

                <FormRow>
                  <FormField
                    label="Descripción"
                    name="description"
                    register={restaurantForm.register}
                    error={restaurantForm.formState.errors.description}
                    placeholder="Describe tu restaurante, especialidades, ambiente..."
                  />
                </FormRow>
              </FormGrid>
            </FormFields>
            {isEditingRestaurant && (
              <ButtonGroup>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    restaurantForm.reset();
                    setSelectedCountry(profile?.country);
                    setSelectedCity(profile?.city);
                    setIsCustomRegion(false);
                    setError(null);
                    setIsEditingRestaurant(false);
                  }}
                  disabled={isSubmittingRestaurant}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmittingRestaurant}
                  loading={isSubmittingRestaurant}
                >
                  Guardar Restaurante
                </Button>
              </ButtonGroup>
            )}
          </form>
        </Card>
      </ProfileSection>

      {/* User Profile Section */}
      <ProfileSection>
        <Card>
          <SectionHeader>
            <SectionTitle>
              <SectionIcon role="img" aria-label="Usuario">
                <Icon name="profile" size="lg" />
              </SectionIcon>
              Configuración Personal
            </SectionTitle>
            {!isEditingUser && (
              <Button
                variant="secondary"
                startIcon={<Icon name="edit" size="sm" />}
                aria-label="Editar configuración personal"
                disabled={isEditingRestaurant}
                onClick={() => {
                  setError(null);
                  setDraftTheme(theme);
                  setIsEditingUser(true);
                }}
              >
                Editar
              </Button>
            )}
          </SectionHeader>

          {isEditingUser && (
            <PageDescription style={{ marginBottom: '1rem' }}>
              Los datos personales se guardan en esta sesión. El tema se
              conserva en este navegador.
            </PageDescription>
          )}

          <form onSubmit={userForm.handleSubmit(onSubmitUser)}>
            <FormFields
              disabled={!isEditingUser || isSubmittingUser}
              aria-label="Configuración personal"
            >
              <FormGrid>
                <FormField
                  label="Nombre"
                  name="name"
                  register={userForm.register}
                  error={userForm.formState.errors.name}
                  placeholder="Tu nombre completo"
                />

                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  register={userForm.register}
                  error={userForm.formState.errors.email}
                  required
                  disabled
                  helperText="El email no se puede cambiar por motivos de seguridad"
                />

                <FormField
                  label="Teléfono Personal"
                  name="phone"
                  register={userForm.register}
                  error={userForm.formState.errors.phone}
                  placeholder="Tu número de teléfono"
                />

                <Controller
                  name="language"
                  control={userForm.control}
                  render={({ field, fieldState }) => (
                    <SelectField
                      name="language"
                      label="Idioma"
                      options={languages}
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                      placeholder="Selecciona tu idioma"
                    />
                  )}
                />

                <FormRow>
                  <ThemeToggleContainer>
                    <ThemeToggleLabel>Modo de Tema</ThemeToggleLabel>
                    <DarkModeToggle
                      value={draftTheme}
                      onThemeChange={setDraftTheme}
                      variant="button"
                      size="md"
                      showSystemOption={true}
                    />
                    <ThemeToggleDescription>
                      Elige entre tema claro, oscuro, o automático según las
                      preferencias de tu sistema.
                    </ThemeToggleDescription>
                  </ThemeToggleContainer>
                </FormRow>

                <FormRow>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginTop: '1rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      id="notifications_enabled"
                      {...userForm.register('notifications_enabled')}
                      style={{ width: 'auto' }}
                    />
                    <label htmlFor="notifications_enabled">
                      Recibir notificaciones por email
                    </label>
                  </div>
                </FormRow>
              </FormGrid>
            </FormFields>
            {isEditingUser && (
              <ButtonGroup>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    userForm.reset();
                    setDraftTheme(theme);
                    setError(null);
                    setIsEditingUser(false);
                  }}
                  disabled={isSubmittingUser}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmittingUser}
                  loading={isSubmittingUser}
                >
                  Guardar Configuración
                </Button>
              </ButtonGroup>
            )}
          </form>
        </Card>
      </ProfileSection>
    </PageContainer>
  );
};
